import * as React from "react";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { capitalize } from "@utdanningsdirektoratet/utils/string";
import styles from "./Button.module.less";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  primary?: boolean;
  wrapped?: boolean;
  last?: boolean;
  naked?: boolean;
  inline?: boolean;
  size?: "normal" | "small";
  color?: "none" | "green" | "red" | "white" | "vy";
}

type Filter<T, U> = T extends U ? T : never;

const preventDefault = (event: React.KeyboardEvent) => {
  if (event.key === " " || event.key === "Enter") {
    event.preventDefault();
  }
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = "normal",
      active,
      color = "none",
      primary,
      wrapped,
      last,
      naked,
      disabled,
      inline,
      type = "button",
      className,
      onClick,
      onKeyDown,
      children,
      ...rest
    },
    ref
  ) => {
    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === " " || event.key === "Enter") {
        if (onClick) {
          event.preventDefault();
          onClick(event as Filter<React.KeyboardEvent<HTMLButtonElement>, React.MouseEvent<HTMLButtonElement>>);
        }
      }
      if (onKeyDown) {
        onKeyDown(event);
      }
    };

    const buttonClass = cls(
      {
        [styles.button]: !inline,
        [styles.buttonActive]: active,
        [styles.buttonPrimary]: primary,
        [styles.buttonDisabled]: disabled,
        [styles.buttonWrapped]: wrapped,
        [styles.buttonLast]: last,
        [styles.buttonInline]: inline,
        [styles.buttonNaked]: naked,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [styles[`button${capitalize(size)}`]]: size,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [styles[`button${capitalize(color)}`]]: color,
      },
      className
    );

    return (
      <button
        {...rest}
        ref={ref}
        // eslint-disable-next-line react/button-has-type
        type={type}
        className={buttonClass}
        aria-pressed={active}
        aria-disabled={disabled}
        onClick={!disabled ? onClick : (e) => e.preventDefault()}
        onKeyDown={!disabled ? onKeyDownHandler : preventDefault}
      >
        {children}
      </button>
    );
  }
);

export default Button;
